import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon,
  PhotoIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../lib/config';

// API base is provided by shared config (CRA/Next compatible)
const API = API_BASE;

const QualificationRegistration = ({ onBack }) => {
  const [qualifications, setQualifications] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState([
    {
      qualification_name: '',
      obtained_date: '',
      qualification_image: ''
    }
  ]);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchQualifications();
  }, []);

  const fetchQualifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/qualifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setQualifications(data);
      } else {
        toast.error('資格情報の取得に失敗しました');
      }
    } catch (error) {
      console.error('Error fetching qualifications:', error);
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      // ファイルサイズチェック（5MB制限）
      if (file.size > 5 * 1024 * 1024) {
        toast.error('画像ファイルは5MB以下にしてください');
        return;
      }

      // ファイル形式チェック
      if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
        toast.error('JPEG、JPG、PNG形式の画像のみアップロード可能です');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const newFormData = [...formData];
        newFormData[index].qualification_image = e.target.result;
        setFormData(newFormData);
      };
      reader.readAsDataURL(file);
    }
  };

  const addQualificationForm = () => {
    setFormData([...formData, {
      qualification_name: '',
      obtained_date: '',
      qualification_image: ''
    }]);
  };

  const removeQualificationForm = (index) => {
    if (formData.length > 1) {
      const newFormData = formData.filter((_, i) => i !== index);
      setFormData(newFormData);
    }
  };

  const updateFormData = (index, field, value) => {
    const newFormData = [...formData];
    newFormData[index][field] = value;
    setFormData(newFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // バリデーション：少なくとも一つの資格名が入力されているかチェック
    const validForms = formData.filter(form => form.qualification_name.trim());
    if (validForms.length === 0) {
      toast.error('少なくとも一つの資格名を入力してください');
      return;
    }

    setSubmitLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      if (editingId) {
        // 編集モード（単一の資格を更新）
        const response = await fetch(`${API}/qualifications/${editingId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            qualification_name: formData[0].qualification_name,
            obtained_date: formData[0].obtained_date,
            qualification_image: formData[0].qualification_image,
          })
        });

        if (response.ok) {
          toast.success('資格情報を更新しました');
        } else {
          const errorData = await response.json();
          toast.error(errorData.detail || '更新に失敗しました');
        }
      } else {
        // 新規登録モード（複数の資格を一括登録）
        let successCount = 0;
        let errorCount = 0;

        for (const qualification of validForms) {
          try {
            const response = await fetch(`${API}/qualifications`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                qualification_name: qualification.qualification_name,
                obtained_date: qualification.obtained_date,
                qualification_image: qualification.qualification_image,
              })
            });

            if (response.ok) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (error) {
            errorCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount}件の資格を登録しました`);
        }
        if (errorCount > 0) {
          toast.error(`${errorCount}件の資格の登録に失敗しました`);
        }
      }

      // フォームリセット
      setShowForm(false);
      setEditingId(null);
      setFormData([{
        qualification_name: '',
        obtained_date: '',
        qualification_image: ''
      }]);
      fetchQualifications();

    } catch (error) {
      console.error('Error saving qualification:', error);
      toast.error('保存に失敗しました');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (qualification) => {
    setEditingId(qualification.id);
    setFormData([{
      qualification_name: qualification.qualification_name,
      obtained_date: qualification.obtained_date || '',
      qualification_image: qualification.qualification_image || ''
    }]);
    setShowForm(true);
  };

  const handleDelete = async (qualificationId) => {
    if (!window.confirm('この資格を削除しますか？')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/qualifications/${qualificationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('資格を削除しました');
        fetchQualifications();
      } else {
        toast.error('削除に失敗しました');
      }
    } catch (error) {
      console.error('Error deleting qualification:', error);
      toast.error('削除に失敗しました');
    }
  };

  const getStatusBadge = (status, rejectionReason) => {
    switch (status) {
      case 'approved':
        return (
          <div className="flex items-center text-green-600">
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            <span className="text-sm font-medium">承認済み</span>
          </div>
        );
      case 'rejected':
        return (
          <div className="flex flex-col">
            <div className="flex items-center text-red-600">
              <XCircleIcon className="h-4 w-4 mr-1" />
              <span className="text-sm font-medium">却下</span>
            </div>
            {rejectionReason && (
              <div className="text-xs text-red-500 mt-1">
                理由: {rejectionReason}
              </div>
            )}
          </div>
        );
      default:
        return (
          <div className="flex items-center text-yellow-600">
            <ClockIcon className="h-4 w-4 mr-1" />
            <span className="text-sm font-medium">審査中</span>
          </div>
        );
    }
  };

  if (showForm) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* ヘッダー */}
        <div className="bg-white shadow-sm">
          <div className="flex items-center justify-between px-4 py-4">
            <button
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
                setFormData([{
                  qualification_name: '',
                  issuing_organization: '',
                  acquisition_date: '',
                  certificate_image: ''
                }]);
              }}
              className="flex items-center text-gray-600"
            >
              <ChevronLeftIcon className="h-5 w-5 mr-1" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">
              {editingId ? '資格情報編集' : '保有資格の登録'}
            </h1>
            <div className="w-6"></div>
          </div>
        </div>

        {/* フォームコンテンツ */}
        <div className="p-4">
          <div className="mb-6">
            <p className="text-gray-600 text-sm">
              登録する資格を選択し、お持ちの資格証明書の写真を撮影してアップロードしてください。
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {formData.map((form, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                {/* フォーム削除ボタン（複数フォームがある場合のみ表示） */}
                {formData.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeQualificationForm(index)}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                  >
                    <XCircleIcon className="h-5 w-5" />
                  </button>
                )}

                <h4 className="font-medium text-gray-900 mb-4">
                  資格情報 {formData.length > 1 ? `${index + 1}` : ''}
                </h4>

                {/* 登録する資格 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    登録する資格
                  </label>
                  <input
                    type="text"
                    value={form.qualification_name}
                    onChange={(e) => updateFormData(index, 'qualification_name', e.target.value)}
                    placeholder="資格名を入力してください"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* 発行機関（任意） */}
                <div className="mb-4">
                  {/* 発行機関は現在バックエンドモデルにないため非表示。必要ならサーバ側拡張後に再有効化 */}
                </div>

                {/* 取得年月（任意） */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    取得年月（任意）
                  </label>
                  <input
                    type="month"
                    value={form.obtained_date}
                    onChange={(e) => updateFormData(index, 'obtained_date', e.target.value)}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* 写真アップロード */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    写真アップロード
                  </label>
                  
                  {form.qualification_image ? (
                    <div className="relative">
                      <img 
                        src={form.qualification_image} 
                        alt="証明書プレビュー" 
                        className="w-full h-48 object-cover rounded-lg border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => updateFormData(index, 'qualification_image', '')}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                      >
                        <XCircleIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                      <div className="text-center">
                        <PhotoIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <button
                          type="button"
                          onClick={() => document.getElementById(`certificate-upload-${index}`).click()}
                          className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center mx-auto"
                        >
                          写真を選択
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <input
                    id={`certificate-upload-${index}`}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={(e) => handleImageUpload(e, index)}
                    className="hidden"
                  />
                </div>
              </div>
            ))}

            {/* 資格情報を追加するボタン */}
            {!editingId && (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={addQualificationForm}
                  className="bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition-colors flex items-center"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  資格情報を追加する
                </button>
              </div>
            )}

            {/* 注意事項 */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>注意事項</strong><br />
                登録された資格証明書は、資格情報の確認等のために、バイターズが保有し、マッチングした事業者に提供する場合があります。
              </p>
            </div>

            {/* 同意チェックボックス */}
            <div className="flex items-start">
              <input
                type="checkbox"
                id="agree"
                required
                className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="agree" className="ml-2 text-sm text-gray-700">
                注意事項に同意する
              </label>
            </div>

            {/* 登録ボタン */}
            <button
              type="submit"
              disabled={submitLoading}
              className="w-full bg-gray-400 text-white py-3 rounded-lg font-medium hover:bg-gray-500 transition-colors disabled:opacity-50"
            >
              {submitLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {editingId ? '更新中...' : '登録中...'}
                </div>
              ) : (
                editingId ? '更新する' : `${formData.filter(f => f.qualification_name.trim()).length}件の資格を登録する`
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <div className="bg-white shadow-sm">
        <div className="flex items-center justify-between px-4 py-4">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600"
          >
            <ChevronLeftIcon className="h-5 w-5 mr-1" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">保有資格</h1>
          <button
            onClick={() => setShowForm(true)}
            className="text-blue-600 text-sm font-medium"
          >
            + 追加
          </button>
        </div>
      </div>

      {/* コンテンツ */}
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : qualifications.length > 0 ? (
          <div className="space-y-4">
            {qualifications.map((qualification) => (
              <div key={qualification.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{qualification.qualification_name}</h3>
                    {qualification.issuing_organization && (
                      <p className="text-sm text-gray-600 mt-1">{qualification.issuing_organization}</p>
                    )}
                    {qualification.acquisition_date && (
                      <p className="text-sm text-gray-500 mt-1">取得年月: {qualification.acquisition_date}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {qualification.status !== 'approved' && (
                      <>
                        <button
                          onClick={() => handleEdit(qualification)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(qualification.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  {getStatusBadge(qualification.status, qualification.rejection_reason)}
                  {qualification.certificate_image && (
                    <img 
                      src={qualification.certificate_image} 
                      alt="証明書" 
                      className="w-16 h-12 object-cover rounded border"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <PhotoIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">まだ資格が登録されていません</p>
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors"
            >
              最初の資格を登録する
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QualificationRegistration;